import { useEffect, useState } from "react";
import { ShieldAlert, RefreshCcw, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Phase G HOTFIX (27.05.2026) — Admin-only Auth-Health-Check.
 *
 * Diagnoses common auth-token desynchronization cases (e.g. stale `mt_token`
 * after the cookie-vs-Bearer migration that triggers the
 * "Ihre Sitzung ist abgelaufen" loop). Renders ONLY when:
 *   - The current user is an admin (or super-admin)
 *   - A problem is detected (no noise on green path)
 *
 * Health checks run:
 *   - On mount
 *   - On window `focus` (after switching back to the tab)
 *   - Every 60 s while the user is on the page
 *
 * The "Auth zurücksetzen" button clears localStorage + reloads — the
 * Self-Service version of the support hint we used to give in chat.
 */
export default function AuthHealthBanner({ user }) {
  const [issue, setIssue] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  const isAdmin = user && (user.role === "admin" || user.is_super_admin);

  async function runCheck() {
    if (!isAdmin) return;
    let token = null;
    try { token = localStorage.getItem("mt_token"); } catch { /* ignore */ }

    // Case 1: token expected but missing while context says we have a user
    if (!token) {
      setIssue({
        code: "no_token",
        title: "Auth-Token fehlt",
        detail: "Im Browser-Speicher ist kein Authentifizierungs-Token vorhanden. Folge-Anfragen schlagen wahrscheinlich fehl.",
      });
      return;
    }

    // Case 2: token present but /auth/me does not match the context user
    try {
      const { data } = await api.get("/auth/me", { validateStatus: () => true });
      if (!data || data.id !== user.id) {
        setIssue({
          code: "id_mismatch",
          title: "Sitzung weicht ab",
          detail: "Der Token im Browser gehört zu einer anderen Identität als die aktuell angemeldete. Bitte zurücksetzen.",
        });
        return;
      }
      // All good
      setIssue(null);
    } catch (e) {
      const status = e?.response?.status;
      setIssue({
        code: status === 401 ? "stale_token" : "probe_failed",
        title: status === 401 ? "Token abgelaufen oder ungültig" : "Authentifizierungs-Prüfung fehlgeschlagen",
        detail: status === 401
          ? "Der im Browser gespeicherte Token wird vom Server abgelehnt. Ein Reset stellt die Anmeldung wieder her."
          : "Die Auth-Prüfung gegen das Backend konnte nicht abgeschlossen werden.",
      });
    }
  }

  useEffect(() => {
    if (!isAdmin) return undefined;
    runCheck();
    const onFocus = () => runCheck();
    const interval = setInterval(runCheck, 60_000);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, user?.id]);

  if (!isAdmin || !issue || dismissed) return null;

  async function handleReset() {
    setBusy(true);
    // Phase G HOTFIX v2 (27.05.2026): a hard reset must clear BOTH client
    // state (localStorage tokens) AND the server-side session (httpOnly auth
    // cookie set by /auth/login). Without the explicit POST /auth/logout,
    // the browser's still-valid cookie would let AuthContext re-authenticate
    // on the next /auth/me call and bounce the user straight back into the
    // app — defeating the diagnostic intent of this button.
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    try { localStorage.clear(); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    setTimeout(() => { window.location.assign("/login"); }, 60);
  }

  return (
    <div
      data-testid="auth-health-banner"
      className="border-b border-rose-500/40 bg-rose-500/10 text-rose-100"
    >
      <div className="max-w-screen-2xl mx-auto px-6 py-2.5 flex items-start gap-3">
        <ShieldAlert className="w-4 h-4 mt-0.5 text-rose-400 shrink-0" />
        <div className="flex-1 text-xs leading-snug">
          <span className="font-semibold text-rose-200">Auth-Diagnose · Nur sichtbar für Administratoren:</span>{" "}
          <span className="text-rose-100/90">{issue.title}.</span>{" "}
          <span className="text-rose-100/70">{issue.detail}</span>
        </div>
        <button
          type="button"
          onClick={handleReset}
          disabled={busy}
          data-testid="auth-health-reset-btn"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-50"
        >
          <RefreshCcw className="w-3 h-3" />
          {busy ? "Setze zurück…" : "Auth zurücksetzen"}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="auth-health-dismiss-btn"
          className="inline-flex items-center justify-center w-6 h-6 rounded text-rose-300 hover:text-rose-100 hover:bg-rose-500/20"
          title="Hinweis vorübergehend ausblenden"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
